import { imageCacheUrl } from "./constants";
import * as Data from "./data";
import { PhotoMeta, Vehicle } from "./entities";
import { photoAgeToHuman } from "./extensions";

const photoAspectRatio = 142 / 225;
const secondsInDay = 60 * 60 * 24;

let pendingFrame: number | null = null;

export function getImageCacheUrl(documentationId: number, width: number, quality: number) {
  return `${imageCacheUrl}/?documentationId=${documentationId}&width=${width}&quality=${quality}`;
}

export function renderVehiclePhoto(block: HTMLElement, container: HTMLElement, vehicle: Vehicle) {
  if (pendingFrame !== null) {
    cancelAnimationFrame(pendingFrame);
    pendingFrame = null;
  }

  if (!vehicle.lastPhotoId) {
    block.classList.add("invisible");
    container.replaceChildren();
    return;
  }

  block.classList.remove("invisible");

  const documentationId = vehicle.lastPhotoId;
  const alt = `Fotografie vozu ${vehicle.registrationNumber}`;

  const render = () => {
    const width = Math.floor(container.getBoundingClientRect().width);
    if (width <= 0) {
      pendingFrame = requestAnimationFrame(render);
      return;
    }

    pendingFrame = null;
    buildPhoto(container, documentationId, alt, width);
  };

  pendingFrame = requestAnimationFrame(render);
}

function buildPhoto(container: HTMLElement, documentationId: number, alt: string, width: number) {
  const height = Math.round(width * photoAspectRatio);

  const wrapper = document.createElement("div");
  wrapper.className = "image-wrapper";

  const link = document.createElement("a");
  link.className = "image-container";
  link.href = `https://seznam-autobusu.cz/dokumentacka/${documentationId}`;
  link.target = "_blank";
  link.rel = "noopener noreferrer";

  const placeholder = document.createElement("div");
  placeholder.className = "image-loading-placeholder";
  placeholder.style.width = `${width}px`;
  placeholder.style.height = `${height}px`;

  for (const position of ["top-left", "top-right", "bottom-left", "bottom-right"]) {
    const corner = document.createElement("div");
    corner.className = `corner ${position}`;
    placeholder.append(corner);
  }

  const altText = document.createElement("div");
  altText.className = "loading-alt-text";
  altText.textContent = alt;
  placeholder.append(altText);

  const image = document.createElement("img");
  image.alt = alt;
  image.width = width;
  image.height = height;
  image.className = "loading";

  link.append(placeholder, image);
  wrapper.append(link);

  image.onload = () => {
    placeholder.remove();
    image.classList.remove("loading");
  };

  image.onerror = () => {
    const error = document.createElement("div");
    error.className = "last-photo-error";
    error.textContent = "Nelze načíst obrázek";
    container.replaceChildren(error);
  };

  container.replaceChildren(wrapper);
  image.src = getImageCacheUrl(documentationId, Math.round(width * Math.min(window.devicePixelRatio, 3)), 80);

  loadPhotoMeta(wrapper, documentationId);
}

async function loadPhotoMeta(wrapper: HTMLElement, documentationId: number) {
  try {
    const meta: PhotoMeta = await Data.fleetoraClient.getPhotoMeta(documentationId.toString());
    const metaElement = document.createElement("div");
    metaElement.className = "meta";

    if (meta.age) {
      const [value, unit] = photoAgeToHuman(meta.age * secondsInDay);
      const date = document.createElement("span");
      date.className = "date";
      date.textContent = `Před ${value} ${unit}`;
      metaElement.append(date);
    }

    if (meta.author) {
      const copyright = document.createElement("span");
      copyright.className = "copyright";
      copyright.textContent = `© ${meta.author}`;
      metaElement.append(copyright);
    }

    if (metaElement.childElementCount > 0) {
      wrapper.append(metaElement);
    }
  } catch (err) {
    console.error("[Photo] Failed to load photo meta", err);
  }
}
